import React, {useContext, useEffect, useState} from 'react';
import {
  Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import Widget from '../components/Widget';
import { ArrowBack, Save, Visibility } from '@mui/icons-material';
import apis from '../../../apis';
import { useToastContext } from '../../../components/Toast/toastContext';
import styles from '../styles';
import {AppContext} from '../../../context';
import { ITheme } from '../../../resource/interfaces';
import { ThemeSelector } from '../../../components/ThemeSelector';
import { WidgetPreviewDialog } from '../../../components/Dialogs/WidgetPreview';

export const WidgetEditPage = () => {
  const { isAdmin, account } = useContext(AppContext);
  const navigate = useNavigate();
  const { widgetId } = useParams<{ widgetId: string }>();
  const { activeToast } = useToastContext();

  const [widget, setWidget] = useState<any>(null);
  const [originalWidget, setOriginalWidget] = useState<any>(null);
  const [preview, setPreview] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [themes, setThemes] = useState<ITheme[]>([]);
  const [themesLoading, setThemesLoading] = useState<boolean>(false);
  const [selectedThemeId, setSelectedThemeId] = useState<string>('');
  const [selectedTheme, setSelectedTheme] = useState<ITheme | null>(null);
  // Popover states removed (ThemeSelector handles preview)
  const [isin, setIsin] = useState<string>('');
  const [name, setName] = useState<string>('');

  const goBack = () => {
    navigate(-1);
  };

  const handleOpenPreview = () => {
    if (!widget) {
      return;
    }
    setOpenPreviewDialog(true);
  };

  const handleClosePreview = () => {
    setOpenPreviewDialog(false);
  };

  const onChangeWidget = (widget) => {
    setWidget(widget);
  };

  const loadThemes = async () => {
    setThemesLoading(true);
    try {
      const result = await apis.theme.getThemes();
      if (result.success) {
        setThemes(result.themes);
      } else {
        activeToast({ type: 'error', text: result.msg || 'Failed to load themes' });
      }
    } catch (error) {
      activeToast({ type: 'error', text: 'Failed to load themes' });
    } finally {
      setThemesLoading(false);
    }
  };

  const editWidget = () => {
    apis.cdn.updateCdnWidget(widget._id, {
      ...widget,
      theme: selectedThemeId,
      isin: isin,
      name: name,
    }, !isAdmin ? account?.id : undefined)
      .then((res: any) => {
        if (res.status) {
          activeToast({
            type: 'success',
            text: res.msg,
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    loadThemes();
  }, []);

  useEffect(() => {
    if (widgetId) {
      setLoading(true);

      apis.cdn.getCdnWidgetByWidgetId(widgetId, !isAdmin ? account?.id : undefined)
        .then((res) => {
          if (res) {
            setLoading(false);
            setWidget(res);
            setOriginalWidget(res);
            setIsin(res.isin);
            setName(res.name);
            if (res.theme) {
              setSelectedTheme(res.theme);
              setSelectedThemeId(res.theme._id); // Fallback to first theme if no theme set
            }
          }
        })
        .catch((err) => {
          console.log('catch>>>', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    // Update selectedTheme when selectedThemeId changes
    const theme = themes.find(t => t._id === selectedThemeId);
    setSelectedTheme(theme || null);
  }, [selectedThemeId, themes]);

  const handleChangeThemeId = (themeId: string) => {
    setSelectedThemeId(themeId);
  };

  const handleChangeIsin = (e) => {
    setIsin(e.target.value);
  };

  const handleChangeName = (e) => {
    setName(e.target.value);
  };

  // ColorSetPreview and popover handlers removed (ThemeSelector includes its own preview UI)

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={20}>
        <Typography variant="h5">Edit Widget</Typography>
        <Box display="flex" alignItems="center" gap={10}>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handleOpenPreview}
            disabled={!widget}
          >
            Preview
          </Button>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={goBack}>Back</Button>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={30}>
        <TextField
          label="NAME"
          fullWidth
          value={name || ""}
          onChange={handleChangeName}
          sx={styles.isinInput}
        />
        <TextField
          label="ISIN"
          fullWidth
          value={isin || ""}
          onChange={handleChangeIsin}
          sx={styles.isinInput}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" marginY={20}>
        <ThemeSelector
          selectedThemeId={selectedThemeId}
          onChange={handleChangeThemeId}
        />
      </Box>

      <Box>
        {loading ? (
          <Box height="100%" display="flex" justifyContent="center" alignItems="center">
            <CircularProgress size={56} />
          </Box>
        ) : (
          <Widget
            isin={isin}
            widget={widget}
            originalWidget={originalWidget}
            onChangeWidget={onChangeWidget}
            preview={preview}
            theme={selectedTheme}
            themeColor={selectedTheme?.mainColor || widget?.color || '#1976d2'}
          />
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <Button
          startIcon={<Save />}
          onClick={editWidget}
          disabled={!widget}
        >
          Save
        </Button>
      </Box>

      {!!widget && openPreviewDialog && (
        <WidgetPreviewDialog
          open={openPreviewDialog}
          selectedWidget={widget}
          onClose={handleClosePreview}
        />
      )}
    </Box>
  )
};
